import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MarketAlertItem } from './shared/types';
import { apiClient } from './api/client';
import { SidebarRail, CANONICAL_VIEWS, REALM_VIEWS } from './components/SidebarRail';
import { TopHeader } from './components/TopHeader';
import { MarketRealm } from './components/common/MarketRealmSwitcher';
import { GlobalAgentCopilotHub } from './components/GlobalAgentCopilotHub';
import { AlertsDrawer } from './components/AlertsDrawer';
import { CryptoAIBriefView } from './features/brief/CryptoAIBriefView';
import { RegimeMatrixView } from './features/regime/RegimeMatrixView';
import { TrendWeatherGrid } from './features/regime/TrendWeatherGrid';
import { RegimeDecisionBacktestView } from './features/regime/RegimeDecisionBacktestView';
import { IndicatorsMatrixView } from './features/indicators/IndicatorsMatrixView';
import { MarketFeedsView } from './features/basis/MarketFeedsView';
import {
  AShareScreenerView,
  AShareWatchlistView,
  AShareIndexRadarView,
  AShareAmbushRadarView,
  AShareCTMatrixView,
  ASharePatternTagsView,
  AShareSectorRotationView,
  AShareMainProfileView,
} from './features/ashare';
import { MacroMarketView, GlobalMacroRegimeView } from './features/macro';
import { MultiAssetHealthCenter } from './features/telemetry/MultiAssetHealthCenter';
import { DrAuditLogView } from './components/DrAuditLogView';
import { DataCenterHub } from './components/DataCenterHub';
import { ProjectGroundingView } from './features/grounding/ProjectGroundingView';
import { IntegrationsAuditHub } from './features/integrations/IntegrationsAuditHub';
import { SettingsView } from './components/SettingsView';
import { MultiAssetMarketBoardPreview } from './features/market/MultiAssetMarketBoardPreview';
import { TaskDispatcherCockpit } from './features/dispatcher';
import { CryptoStrategistView } from './features/crypto-strategist/CryptoStrategistView';
import { TradeAdvisoryBlueprintView } from './features/advisory/TradeAdvisoryBlueprintView';
import { TacticalCombatDeckView } from './features/crypto-deck/TacticalCombatDeckView';

export interface AppFreshnessResult {
  category: 'fresh' | 'stale' | 'interrupted' | 'unavailable' | 'invalid';
  freshnessText: string;
  freshnessColor: string;
  freshnessTooltip: string;
  isAlert: boolean;
  diffSec: number | null;
}

export function evaluateAppFreshness(
  probeUtc: string | null | undefined,
  nowMs: number = Date.now()
): AppFreshnessResult {
  if (!probeUtc) {
    return {
      category: 'unavailable',
      freshnessText: 'N/A (无时效探针)',
      freshnessColor: 'var(--color-bearish)',
      freshnessTooltip: '未接收到权威 UTC 探针时间戳',
      isAlert: true,
      diffSec: null,
    };
  }
  const lastDate = new Date(probeUtc);
  const parsedTime = lastDate.getTime();
  if (isNaN(parsedTime) || parsedTime > nowMs + 1000) {
    return {
      category: 'invalid',
      freshnessText: '✖ 时间戳异常',
      freshnessColor: 'var(--color-bearish)',
      freshnessTooltip: `时间戳解析失败或为未来时间 (${probeUtc})`,
      isAlert: true,
      diffSec: null,
    };
  }
  const diffSec = (nowMs - parsedTime) / 1000;
  const probeTimeStr = lastDate.toLocaleTimeString('zh-CN', { timeZone: 'UTC', hour12: false });
  if (diffSec < 60.0) {
    return {
      category: 'fresh',
      freshnessText: '● 极鲜实时',
      freshnessColor: 'var(--color-emerald)',
      freshnessTooltip: `数据流时效正常 (当前延时: ${diffSec.toFixed(1)}s) · 探针时间: ${probeTimeStr} UTC`,
      isAlert: false,
      diffSec,
    };
  } else if (diffSec < 300.0) {
    const delaySec = Math.round(diffSec);
    return {
      category: 'stale',
      freshnessText: `▲ 延迟预警 (${delaySec}s)`,
      freshnessColor: 'var(--color-warning)',
      freshnessTooltip: `数据链路偶发拥塞或源端延迟 (延时: ${delaySec}s) · 探针时间: ${probeTimeStr} UTC`,
      isAlert: true,
      diffSec,
    };
  } else {
    const delayMin = Math.floor(diffSec / 60);
    return {
      category: 'interrupted',
      freshnessText: `✖ 数据中断 (>${delayMin}m)`,
      freshnessColor: 'var(--color-bearish)',
      freshnessTooltip: `上游行情流已逾期 ${delayMin} 分钟无最新报价，请排查网络链路`,
      isAlert: true,
      diffSec,
    };
  }
}

export interface AppProps {
  initialRealm?: MarketRealm;
  initialFreshness?: { text: string; color: string; tooltip?: string };
  initialCryptoCompleteness?: string;
  initialCryptoMissingCount?: number | null;
  initialAshareCoverage?: string;
  initialAshareCoveragePct?: string;
  initialEodSyncedDate?: string | null;
  initialWorkerStatus?: string;
}

// Ensure canonical inline favicon to prevent browser 404 console errors on /favicon.ico
if (typeof document !== 'undefined') {
  let favicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/svg+xml';
    favicon.href = 'data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>%F0%9F%93%88</text></svg>';
    document.head.appendChild(favicon);
  }
}

/**
 * Canonicalizes legacy and non-canonical hashes to modern 4+1 system realm URLs.
 * Returns { canonicalHash, viewId, realm } if a canonical redirection is required, or null if already canonical/unrelated.
 */
export function canonicalizeSystemHash(rawHash: string): { canonicalHash: string; viewId: string; realm: MarketRealm } | null {
  const hashWithoutHash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
  const [pathPart, queryPart] = hashWithoutHash.split('?');
  const normalizedPath = (pathPart || '').toLowerCase();
  const searchParams = new URLSearchParams(queryPart || '');

  // 1. #/system/dispatcher -> #/system/pipeline
  if (normalizedPath === '/system/dispatcher' || normalizedPath === '/dispatcher' || normalizedPath === 'dispatcher') {
    const q = searchParams.toString();
    return {
      canonicalHash: `#/system/pipeline${q ? `?${q}` : ''}`,
      viewId: 'view-dispatcher-cockpit',
      realm: 'system',
    };
  }

  // 2. #/multiasset/health -> #/system/infra?tab=multiasset
  if (normalizedPath === '/multiasset/health') {
    searchParams.set('tab', 'multiasset');
    return {
      canonicalHash: `#/system/infra?${searchParams.toString()}`,
      viewId: 'view-infra-telemetry',
      realm: 'system',
    };
  }

  // 3. #/audit -> #/system/audit
  if (normalizedPath === '/audit' || normalizedPath === 'audit' || normalizedPath === '/dr' || normalizedPath === 'dr') {
    const q = searchParams.toString();
    return {
      canonicalHash: `#/system/audit${q ? `?${q}` : ''}`,
      viewId: 'view-dr-audit',
      realm: 'system',
    };
  }

  // 4. #/telemetry -> #/system/infra (preserving existing query params)
  if (normalizedPath === '/telemetry' || normalizedPath === 'telemetry' || normalizedPath === '/health' || normalizedPath === '/infra' || normalizedPath === 'infra') {
    const q = searchParams.toString();
    return {
      canonicalHash: `#/system/infra${q ? `?${q}` : ''}`,
      viewId: 'view-infra-telemetry',
      realm: 'system',
    };
  }

  // 5. #/gateway -> #/system/gateway (preserving query params such as tab=mailbox)
  if (normalizedPath === '/gateway' || normalizedPath === 'gateway' || normalizedPath === '/integration' || normalizedPath === 'integration') {
    const q = searchParams.toString();
    return {
      canonicalHash: `#/system/gateway${q ? `?${q}` : ''}`,
      viewId: 'view-integrations-audit',
      realm: 'system',
    };
  }

  // 6. #/grounding -> #/system/gateway?tab=grounding
  if (normalizedPath === '/grounding' || normalizedPath === 'grounding') {
    searchParams.set('tab', 'grounding');
    return {
      canonicalHash: `#/system/gateway?${searchParams.toString()}`,
      viewId: 'view-integrations-audit',
      realm: 'system',
    };
  }

  // 6b. #/capabilities or #/system/capabilities -> #/system/gateway?tab=capabilities
  if (normalizedPath === '/capabilities' || normalizedPath === 'capabilities' || normalizedPath === '/system/capabilities') {
    searchParams.set('tab', 'capabilities');
    return {
      canonicalHash: `#/system/gateway?${searchParams.toString()}`,
      viewId: 'view-integrations-audit',
      realm: 'system',
    };
  }

  // 7. #/settings -> #/system/settings
  if (normalizedPath === '/settings' || normalizedPath === 'settings') {
    const q = searchParams.toString();
    return {
      canonicalHash: `#/system/settings${q ? `?${q}` : ''}`,
      viewId: 'view-settings',
      realm: 'system',
    };
  }

  return null;
}

export const App: React.FC<AppProps> = ({
  initialRealm,
  initialFreshness,
  initialCryptoCompleteness,
  initialCryptoMissingCount,
  initialAshareCoverage,
  initialAshareCoveragePct,
  initialEodSyncedDate,
  initialWorkerStatus,
}) => {
  const [activeView, setActiveView] = useState<string>(() => {
    try {
      const hash = typeof window !== 'undefined' ? window.location.hash.toLowerCase() : '';
      if (hash === '#/regime' || hash === '#regime' || hash === '#/crypto/regime') return 'view-regime-matrix';
      if (hash === '#/weather' || hash === '#weather' || hash === '#/crypto/weather') return 'view-trend-weather';
      if (hash === '#/indicators' || hash === '#indicators' || hash === '#/crypto/indicators') return 'view-indicators-matrix';
      if (hash === '#/feeds' || hash === '#feeds' || hash === '#/crypto/feeds') return 'view-market-feeds';
      if (hash.includes('regime-decision') || hash.includes('backtest')) {
        if (hash.includes('ashare')) return 'view-backtest-ashare';
        if (hash.includes('usequity')) return 'view-backtest-usequity';
        if (hash.includes('macro')) return 'view-backtest-macro';
        return 'view-backtest-crypto';
      }
      if (hash.includes('market-board') || hash.includes('market-board-preview')) return 'view-market-board-preview';
      if (hash.includes('tactical-deck') || hash.includes('tactical_deck')) return 'view-crypto-tactical-deck';
      if (hash.includes('trade-advisory') || hash.includes('trade_advisory')) return 'view-trade-advisory';
      if (hash.includes('strategist')) return 'view-crypto-strategist';
      if (hash.includes('brief')) return 'view-crypto-brief';
      if (hash.startsWith('#/ashare')) return 'view-ashare-screener';
      if (hash.startsWith('#/macro')) return 'view-macro-market';
      if (hash === '#/system/pipeline' || hash === '#/system/dispatcher' || hash === '#dispatcher' || hash === '#/dispatcher') return 'view-dispatcher-cockpit';
      if (hash === '#/system/infra' || hash === '#/telemetry' || hash === '#telemetry' || hash === '#/infra' || hash === '#infra' || hash === '#/multiasset/health' || hash === '#/health') return 'view-infra-telemetry';
      if (hash === '#/system/audit' || hash === '#/audit' || hash === '#audit' || hash === '#/dr' || hash === '#dr') return 'view-dr-audit';
      if (hash === '#/system/gateway' || hash === '#/gateway' || hash === '#gateway' || hash === '#/integration' || hash === '#integration' || hash === '#/grounding' || hash === '#grounding') return 'view-integrations-audit';
      if (hash === '#/system/settings' || hash === '#/settings' || hash === '#settings') return 'view-settings';
      if (hash.startsWith('#/system')) return 'view-dispatcher-cockpit';
      return 'view-crypto-brief';
    } catch {
      return 'view-crypto-brief';
    }
  });
  const [currentRealm, setCurrentRealm] = useState<MarketRealm>(() => {
    if (initialRealm) return initialRealm;
    try {
      const hash = typeof window !== 'undefined' ? window.location.hash : '';
      if (hash.startsWith('#/system')) return 'system';
      if (hash.startsWith('#/backtest')) return 'backtest';
      if (hash.startsWith('#/macro')) return 'macro';
      if (hash.startsWith('#/ashare')) return 'ashare';
      return 'crypto';
    } catch {
      return 'crypto';
    }
  });
  const currentRealmRef = useRef<MarketRealm>(currentRealm);

  useEffect(() => {
    currentRealmRef.current = currentRealm;
  }, [currentRealm]);
  const [colorMode, setColorMode] = useState<'mainland' | 'international'>(() => {
    try {
      const saved = localStorage.getItem('market_hub_color_mode');
      return saved === 'international' ? 'international' : 'mainland';
    } catch {
      return 'mainland';
    }
  });

  const handleToggleColorMode = useCallback(() => {
    setColorMode((prev) => {
      const next = prev === 'mainland' ? 'international' : 'mainland';
      try {
        localStorage.setItem('market_hub_color_mode', next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('tso_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);
  const [isAlertsOpen, setIsAlertsOpen] = useState<boolean>(false);

  // Dynamic real-time health and operational telemetry state
  const [alerts, setAlerts] = useState<MarketAlertItem[]>([]);
  const [resolvedAlertIds, setResolvedAlertIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('market_hub_resolved_alert_ids');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [freshnessText, setFreshnessText] = useState<string>(() => initialFreshness?.text || '● 同步中...');
  const [freshnessColor, setFreshnessColor] = useState<string>(() => initialFreshness?.color || 'var(--text-muted)');
  const [freshnessTooltip, setFreshnessTooltip] = useState<string>(() => initialFreshness?.tooltip || '时效正常 · 正在后台静默探测');
  const [sysLoadText, setSysLoadText] = useState<string>('⚡ 探测中...');
  const [sysLoadColor, setSysLoadColor] = useState<string>('var(--text-muted)');
  const [lastCycleUtc, setLastCycleUtc] = useState<string | null>(null);

  // Hardware Infrastructure Telemetry (TopHeader)
  const [cpuPercent, setCpuPercent] = useState<number | null>(null);
  const [ramPercent, setRamPercent] = useState<number | null>(null);
  const [diskPercent, setDiskPercent] = useState<number | null>(null);
  const [uptimeText, setUptimeText] = useState<string>('UP --');

  // Operational Database & Worker Telemetry (Footer) - Truthful initialization (Zero Mock)
  const [opTelemetry, setOpTelemetry] = useState(() => ({
    cryptoDbConnected: null as boolean | null,
    cryptoStoreId: 'N/A',
    multiAssetDbConnected: null as boolean | null,
    multiAssetStoreId: 'N/A',
    workerStatus: initialWorkerStatus !== undefined ? initialWorkerStatus : 'N/A',
    eodSyncedDate: initialEodSyncedDate !== undefined ? initialEodSyncedDate : null,
    eventLoopLagMs: null as number | null,
  }));

  // Telemetry Quality & Completeness (Footer) - Crypto vs A-Share
  const [cryptoCompleteness, setCryptoCompleteness] = useState<string>(() => initialCryptoCompleteness ?? 'N/A');
  const [cryptoMissingCount, setCryptoMissingCount] = useState<number | null>(() => initialCryptoMissingCount !== undefined ? initialCryptoMissingCount : null);
  const [ashareCoverage, setAshareCoverage] = useState<string>(() => initialAshareCoverage ?? 'N/A');
  const [ashareCoveragePct, setAshareCoveragePct] = useState<string>(() => initialAshareCoveragePct ?? 'N/A');
  const [ashareSessionStatus, setAshareSessionStatus] = useState<string>('N/A');

  const [theme, setTheme] = useState<'daylight' | 'dark'>(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const qTheme = urlParams.get('theme');
      if (qTheme === 'dark' || qTheme === 'daylight') return qTheme;
      const saved = localStorage.getItem('market_hub_theme');
      return saved === 'dark' || saved === 'daylight' ? saved : 'daylight';
    } catch {
      return 'daylight';
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (theme === 'daylight') {
      document.body.classList.add('theme-daylight');
    } else {
      document.body.classList.remove('theme-daylight');
    }
    try {
      localStorage.setItem('market_hub_theme', theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const handleToggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'daylight' ? 'dark' : 'daylight'));
  }, []);

  const handleSelectRealm = useCallback((realm: MarketRealm) => {
    setCurrentRealm(realm);
    if (realm === 'crypto') {
      window.location.hash = '#/crypto/brief';
      setActiveView('view-crypto-brief');
    } else if (realm === 'ashare') {
      window.location.hash = '#/ashare/screener';
      setActiveView('view-ashare-screener');
    } else if (realm === 'macro') {
      window.location.hash = '#/macro';
      setActiveView('view-macro-market');
    } else if (realm === 'backtest') {
      window.location.hash = '#/backtest/crypto';
      setActiveView('view-backtest-crypto');
    } else if (realm === 'system') {
      window.location.hash = '#/system/pipeline';
      setActiveView('view-dispatcher-cockpit');
    }
  }, []);

  // Synchronize view from route / hash
  const syncViewFromUrl = useCallback(() => {
    const rawHash = window.location.hash.toLowerCase();
    const hash = rawHash.split('?')[0];
    const pathname = window.location.pathname.toLowerCase();

    // Canonicalize legacy system routes and update window hash without loops
    const redirect = canonicalizeSystemHash(rawHash);
    if (redirect) {
      setActiveView(redirect.viewId);
      setCurrentRealm(redirect.realm);
      if (window.location.hash !== redirect.canonicalHash) {
        window.history.replaceState(null, '', redirect.canonicalHash);
      }
      return;
    }

    if (hash === '#/crypto/brief' || hash === '#/brief' || hash === '#brief' || hash === '#/crypto/ai-brief' || hash === '#/ai-brief' || hash === '#ai-brief') {
      setActiveView('view-crypto-brief');
      setCurrentRealm('crypto');
    } else if (hash.startsWith('#/crypto/tactical-deck') || hash.startsWith('#/tactical-deck') || hash === '#tactical-deck') {
      setActiveView('view-crypto-tactical-deck');
      setCurrentRealm('crypto');
    } else if (hash.startsWith('#/crypto/trade-advisory') || hash.startsWith('#/trade-advisory') || hash === '#trade-advisory') {
      setActiveView('view-trade-advisory');
      setCurrentRealm('crypto');
    } else if (hash === '#/crypto/strategist' || hash === '#/strategist' || hash === '#strategist') {
      setActiveView('view-crypto-strategist');
      setCurrentRealm('crypto');
    } else if (hash === '#/crypto/market-board' || hash === '#/market-board-preview' || hash === '#/market' || hash === '#market') {
      setActiveView('view-market-board-preview');
      setCurrentRealm('crypto');
    } else if (hash === '#/crypto/weather' || hash === '#/weather' || hash === '#weather') {
      setActiveView('view-trend-weather');
      setCurrentRealm('crypto');
    } else if (hash === '#/crypto/indicators' || hash === '#/indicators' || hash === '#indicators') {
      setActiveView('view-indicators-matrix');
      setCurrentRealm('crypto');
    } else if (hash === '#/crypto/feeds' || hash === '#/feeds' || hash === '#feeds' || hash === '#/basis' || hash === '#basis') {
      setActiveView('view-market-feeds');
      setCurrentRealm('crypto');
    } else if (hash === '#/crypto/regime' || hash === '#/regime' || hash === '#regime') {
      setActiveView('view-regime-matrix');
      setCurrentRealm('crypto');
    } else if (hash === '#/crypto/regime-decision' || hash === '#/crypto/backtest' || hash === '#/regime-decision' || hash === '#regime-decision') {
      setActiveView('view-backtest-crypto');
      setCurrentRealm('backtest');
    } else if (hash === '#/backtest/crypto' || hash === '#/backtest' || hash === '#backtest') {
      setActiveView('view-backtest-crypto');
      setCurrentRealm('backtest');
    } else if (hash === '#/backtest/ashare') {
      setActiveView('view-backtest-ashare');
      setCurrentRealm('backtest');
    } else if (hash === '#/backtest/usequity') {
      setActiveView('view-backtest-usequity');
      setCurrentRealm('backtest');
    } else if (hash === '#/backtest/macro') {
      setActiveView('view-backtest-macro');
      setCurrentRealm('backtest');
    } else if (hash === '#/ashare/screener' || hash === '#/ashare' || hash === '#ashare') {
      setActiveView('view-ashare-screener');
      setCurrentRealm('ashare');
    } else if (hash === '#/ashare/watchlist') {
      setActiveView('view-ashare-watchlist');
      setCurrentRealm('ashare');
    } else if (hash === '#/ashare/index-radar') {
      setActiveView('view-ashare-index-radar');
      setCurrentRealm('ashare');
    } else if (hash === '#/ashare/ambush-radar' || hash === '#/ashare/radar') {
      setActiveView('view-ashare-ambush-radar');
      setCurrentRealm('ashare');
    } else if (hash === '#/ashare/ct-matrix' || hash === '#/ashare/ct') {
      setActiveView('view-ashare-ct-matrix');
      setCurrentRealm('ashare');
    } else if (hash === '#/ashare/pattern-tags' || hash === '#/ashare/tags') {
      setActiveView('view-ashare-pattern-tags');
      setCurrentRealm('ashare');
    } else if (hash === '#/ashare/sector-rotation' || hash === '#/ashare/sector') {
      setActiveView('view-ashare-sector-rotation');
      setCurrentRealm('ashare');
    } else if (hash === '#/ashare/main-profile' || hash === '#/ashare/profile') {
      setActiveView('view-ashare-main-profile');
      setCurrentRealm('ashare');
    } else if (hash === '#/macro/regime' || hash === '#/macro' || hash === '#macro') {
      setActiveView('view-macro-regime');
      setCurrentRealm('macro');
    } else if (hash === '#/macro/corridor' || hash === '#/macro/market') {
      setActiveView('view-macro-market');
      setCurrentRealm('macro');
    } else if (hash === '#/system/pipeline' || hash === '#/system/dispatcher' || hash === '#/dispatcher' || hash === '#dispatcher') {
      setActiveView('view-dispatcher-cockpit');
      setCurrentRealm('system');
    } else if (hash === '#/system/infra' || hash === '#/multiasset/health' || hash === '#/health' || hash === '#/telemetry' || hash === '#telemetry' || hash === '#/infra' || hash === '#infra' || pathname.includes('/datacenter')) {
      setActiveView('view-infra-telemetry');
      setCurrentRealm('system');
    } else if (hash === '#/system/audit' || hash === '#/audit' || hash === '#audit' || hash === '#/dr' || hash === '#dr') {
      setActiveView('view-dr-audit');
      setCurrentRealm('system');
    } else if (hash === '#/system/gateway' || hash === '#/gateway' || hash === '#gateway' || hash === '#/integration' || hash === '#integration') {
      setActiveView('view-integrations-audit');
      setCurrentRealm('system');
    } else if (hash === '#/grounding' || hash === '#grounding' || pathname.includes('/copilot')) {
      setActiveView('view-integrations-audit');
      setCurrentRealm('system');
    } else if (hash === '#/system/settings' || hash === '#/settings' || hash === '#settings' || pathname.includes('/settings')) {
      setActiveView('view-settings');
      setCurrentRealm('system');
    } else if (pathname.includes('/alerts')) {
      setIsAlertsOpen(true);
    } else {
      setActiveView('view-regime-matrix');
      setCurrentRealm('crypto');
    }
  }, []);

  useEffect(() => {
    syncViewFromUrl();
    const handleHashChange = () => syncViewFromUrl();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [syncViewFromUrl]);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore when user is focusing form elements
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (e.key === 'Escape') {
        setIsAlertsOpen(false);
        setIsMobileOpen(false);
        return;
      }

      if (e.key === '[') {
        e.preventDefault();
        setIsCollapsed((prev) => {
          const next = !prev;
          try {
            localStorage.setItem('tso_sidebar_collapsed', String(next));
          } catch {
            // ignore
          }
          return next;
        });
        return;
      }

      // Theme toggle: 't' / 'T'
      if ((e.key === 't' || e.key === 'T') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        handleToggleTheme();
        return;
      }

      // Dynamic Realm-Scoped Navigation Shortcuts (Modifier-free single key)
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const key = e.key;
        const activeRealm = currentRealmRef.current || 'crypto';
        const currentViews =
          activeRealm && REALM_VIEWS[activeRealm]
            ? REALM_VIEWS[activeRealm]
            : CANONICAL_VIEWS;

        // Numeric key 0: Settings in realm or global settings
        if (key === '0') {
          e.preventDefault();
          const settingsInRealm = currentViews.find((v) =>
            v.hotkeyHint.split(',').map((s) => s.trim()).includes('0')
          );
          if (settingsInRealm) {
            handleSelectView(settingsInRealm.id);
          } else {
            handleSelectView('view-settings');
          }
          return;
        }

        // Dedicated View 8 Integrations & Service Audit Hub hotkey (Task 020) for non-ashare realm
        if (e.key === '8' && activeRealm !== 'ashare') {
          e.preventDefault();
          handleSelectView('view-integrations-audit');
          return;
        }

        // Numeric keys 1..9: Dispatch dynamically based on active realm views
        if (/^[1-9]$/.test(key)) {
          const matchedView =
            currentViews.find((v) =>
              v.hotkeyHint.split(',').map((s) => s.trim()).includes(key)
            ) || currentViews[parseInt(key, 10) - 1];

          if (matchedView) {
            e.preventDefault();
            handleSelectView(matchedView.id);
          }
          return;
        }

        // Mnemonic letter keys matching hotkeyHint within current realm (e.g. 'w' for weather in crypto, 'g' for grounding in system)
        const lowerKey = key.toLowerCase();
        if (/^[a-z]$/.test(lowerKey)) {
          const mnemonicView = currentViews.find((v) =>
            v.hotkeyHint
              .split(',')
              .map((s) => s.trim().toLowerCase())
              .includes(lowerKey)
          );
          if (mnemonicView) {
            e.preventDefault();
            handleSelectView(mnemonicView.id);
            return;
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleToggleTheme]);

  const handleSelectView = (viewId: string) => {
    setActiveView(viewId);
    setIsMobileOpen(false);

    // Resolve route from current realm views or canonical views
    const allViews = [
      ...Object.values(REALM_VIEWS).flat(),
      ...CANONICAL_VIEWS,
    ];
    const target = allViews.find((v) => v.id === viewId);
    if (target && target.route) {
      const cleanRoute = target.route.replace(/^#\/?/, '');
      window.location.hash = `#/${cleanRoute}`;
      return;
    }

    if (viewId === 'view-ashare-screener') {
      window.location.hash = '#/ashare/screener';
      return;
    }
    if (viewId === 'view-ashare-watchlist') {
      window.location.hash = '#/ashare/watchlist';
      return;
    }
    if (viewId === 'view-ashare-index-radar') {
      window.location.hash = '#/ashare/index-radar';
      return;
    }
    if (viewId === 'view-ashare-ambush-radar') {
      window.location.hash = '#/ashare/ambush-radar';
      return;
    }
    if (viewId === 'view-ashare-ct-matrix') {
      window.location.hash = '#/ashare/ct-matrix';
      return;
    }
    if (viewId === 'view-ashare-pattern-tags') {
      window.location.hash = '#/ashare/pattern-tags';
      return;
    }
    if (viewId === 'view-ashare-sector-rotation') {
      window.location.hash = '#/ashare/sector-rotation';
      return;
    }
    if (viewId === 'view-ashare-main-profile') {
      window.location.hash = '#/ashare/main-profile';
      return;
    }
    if (viewId === 'view-macro-market') {
      window.location.hash = '#/macro';
      return;
    }
    if (viewId === 'view-multiasset-health') {
      window.location.hash = '#/multiasset/health';
      return;
    }
  };

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('tso_sidebar_collapsed', String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  // Real-time System Health & Telemetry Polling (4s cycle)
  const fetchSystemHealth = useCallback(async () => {
    try {
      const [statusRes, teleRes, multiRes] = await Promise.all([
        apiClient.getStatus().catch((err) => {
          console.warn('Status fetch error:', err);
          return null;
        }),
        apiClient.getTelemetry().catch((err) => {
          console.warn('Telemetry fetch error:', err);
          return null;
        }),
        apiClient.get<any>('/api/v1/market/multiasset/health').catch((err) => {
          console.warn('MultiAsset health fetch error:', err);
          return null;
        }),
      ]);

      if (!statusRes && !teleRes && !multiRes) {
        setFreshnessText('✖ 数据中断 (>5m)');
        setFreshnessColor('var(--color-bearish)');
        setFreshnessTooltip('无法连接到系统服务探针端点');
        setSysLoadText('⚡ 服务离线');
        setSysLoadColor('var(--color-bearish)');
        setCpuPercent(null);
        setRamPercent(null);
        setDiskPercent(null);
        setUptimeText('UP --');
        setOpTelemetry({
          cryptoDbConnected: false,
          cryptoStoreId: 'N/A',
          multiAssetDbConnected: false,
          multiAssetStoreId: 'N/A',
          workerStatus: 'DISCONNECTED',
          eodSyncedDate: null,
          eventLoopLagMs: null,
        });
        setCryptoCompleteness('N/A');
        setCryptoMissingCount(null);
        setAshareCoverage('N/A');
        setAshareCoveragePct('N/A');
        setAshareSessionStatus('DISCONNECTED');
        return;
      }

      // 1. Process Hardware Infrastructure Telemetry (TopHeader)
      const sysRes = teleRes?.system_resources as Record<string, unknown> | undefined;
      const cpuVal =
        sysRes?.cpu_percent !== undefined && sysRes?.cpu_percent !== null
          ? Number(sysRes.cpu_percent)
          : null;
      const ramVal =
        (sysRes?.host_ram_percent ?? sysRes?.memory_percent) !== undefined &&
        (sysRes?.host_ram_percent ?? sysRes?.memory_percent) !== null
          ? Number(sysRes?.host_ram_percent ?? sysRes?.memory_percent)
          : null;
      const diskVal =
        (sysRes?.disk_data_percent ?? sysRes?.disk_percent) !== undefined &&
        (sysRes?.disk_data_percent ?? sysRes?.disk_percent) !== null
          ? Number(sysRes?.disk_data_percent ?? sysRes?.disk_percent)
          : null;

      setCpuPercent(cpuVal);
      setRamPercent(ramVal);
      setDiskPercent(diskVal);

      if (teleRes?.uptime_seconds !== undefined && teleRes?.uptime_seconds !== null) {
        const totalSec = Number(teleRes.uptime_seconds);
        const days = Math.floor(totalSec / 86400);
        const hours = Math.floor((totalSec % 86400) / 3600);
        const mins = Math.floor((totalSec % 3600) / 60);
        if (days > 0) {
          setUptimeText(`UP ${days}d ${hours}h`);
        } else if (hours > 0) {
          setUptimeText(`UP ${hours}h ${String(mins).padStart(2, '0')}m`);
        } else {
          setUptimeText(`UP ${mins}m`);
        }
      } else {
        setUptimeText('UP --');
      }

      // 2. Process Operational Database & Worker Telemetry (Footer) - Truthful probe attribution
      const cryptoHealthy = statusRes?.database ? (statusRes.database.healthy === true) : null;
      const dbInfo = statusRes?.database as Record<string, unknown> | undefined;
      const cryptoStoreId = (dbInfo?.store_id as string) || (cryptoHealthy === true ? 'CRYPTO_PRIMARY_ISOLATION' : 'N/A');

      const multiDb = multiRes?.database as Record<string, unknown> | undefined;
      const multiConnected = multiRes?.database ? (multiDb?.connected === true || multiDb?.status === 'CONNECTED') : null;
      const multiAssetStoreId = (multiDb?.store_id as string) || (multiConnected === true ? 'MULTIASSET_ACTIVE' : 'N/A');

      const schedulerInfo = multiRes?.scheduler as Record<string, unknown> | undefined;
      const workerStatus = schedulerInfo?.state
        ? String(schedulerInfo.state).toUpperCase()
        : 'N/A';
      const eodDate = (schedulerInfo?.last_eod_synced_date as string) || null;

      const workerPipeline = teleRes?.worker_pipeline as Record<string, unknown> | undefined;
      const eventLoopLagMs =
        workerPipeline?.event_loop_lag_ms !== undefined && workerPipeline?.event_loop_lag_ms !== null
          ? Number(workerPipeline.event_loop_lag_ms)
          : null;

      setOpTelemetry({
        cryptoDbConnected: cryptoHealthy,
        cryptoStoreId,
        multiAssetDbConnected: multiConnected,
        multiAssetStoreId,
        workerStatus,
        eodSyncedDate: eodDate,
        eventLoopLagMs,
      });

      // 2.1 Calculate Crypto Feed Completeness (Finding 1: Zero synthetic percentage derivation)
      const feedComp = teleRes?.feed_completeness as Record<string, { missing_candle_count?: number; completeness_pct?: string }> | undefined;
      const authoritativeCompPct = (teleRes as Record<string, unknown> | undefined)?.authoritative_completeness_pct as string | undefined;
      if (feedComp && Object.keys(feedComp).length > 0) {
        let sum = 0;
        let allCountsPresent = true;
        let authPct: string | null = null;
        const entries = Object.values(feedComp);
        if (entries.length === 0) {
          allCountsPresent = false;
        }
        for (const c of entries) {
          if (typeof c.missing_candle_count === 'number' && !isNaN(c.missing_candle_count)) {
            sum += c.missing_candle_count;
          } else {
            allCountsPresent = false;
          }
          if (c.completeness_pct) {
            authPct = c.completeness_pct;
          }
        }
        setCryptoMissingCount(allCountsPresent ? sum : null);
        // Task 059 §5.2: Strictly prohibit deriving completeness percentage from missing count!
        setCryptoCompleteness(authoritativeCompPct || authPct || 'N/A');
      } else {
        setCryptoCompleteness('N/A');
        setCryptoMissingCount(null);
      }

      // 2.2 Calculate A-Share Session Timeliness & Coverage (Zero hardcoded counts)
      try {
        const now = new Date();
        const cstTimeParts = now.toLocaleTimeString('en-US', { timeZone: 'Asia/Shanghai', hour12: false }).split(':');
        const cstH = parseInt(cstTimeParts[0] || '0', 10);
        const cstM = parseInt(cstTimeParts[1] || '0', 10);
        const timeInMin = cstH * 60 + cstM;
        const cstDay = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Shanghai' })).getDay();

        const dateSuffix = eodDate ? ` [${eodDate}]` : '';
        let sStatus = `🌙 晚盘复盘池${dateSuffix}`;
        if (cstDay === 0 || cstDay === 6) {
          sStatus = `⏸️ 周末闭市${dateSuffix}`;
        } else if (timeInMin >= 570 && timeInMin < 690) {
          sStatus = `☀️ 早盘交易中 (9:30-11:30)`;
        } else if (timeInMin >= 690 && timeInMin < 780) {
          sStatus = `☀️ 午盘形态池${dateSuffix}`;
        } else if (timeInMin >= 780 && timeInMin < 900) {
          sStatus = `☀️ 下午盘交易中 (13:00-15:00)`;
        } else if (timeInMin >= 900 && timeInMin < 915) {
          sStatus = `⏳ 盘后清算中 (等待 15:15 晚盘池)`;
        } else {
          sStatus = `🌙 晚盘复盘池${dateSuffix}`;
        }
        setAshareSessionStatus(sStatus);

        const authoritativeCoverage = (multiRes as Record<string, unknown> | undefined)?.ashare_coverage as string | undefined;
        const authoritativeCoveragePct = (multiRes as Record<string, unknown> | undefined)?.ashare_coverage_pct as string | undefined;
        setAshareCoverage(authoritativeCoverage || 'N/A');
        setAshareCoveragePct(authoritativeCoveragePct || 'N/A');
      } catch {
        setAshareSessionStatus(eodDate ? `🌙 晚盘复盘池 [${eodDate}]` : '🌙 晚盘复盘池');
        setAshareCoverage('N/A');
        setAshareCoveragePct('N/A');
      }

      // Finding R2-01: Freshness derives strictly from last_price_probe_utc (zero fallback to last_cycle_utc)
      const probeUtc = statusRes?.telemetry_freshness?.last_price_probe_utc || null;
      setLastCycleUtc(probeUtc);
      const appFreshResult = evaluateAppFreshness(probeUtc, Date.now());
      setFreshnessText(appFreshResult.freshnessText);
      setFreshnessColor(appFreshResult.freshnessColor);
      setFreshnessTooltip(appFreshResult.freshnessTooltip);

      // 4. Process System Load Telemetry (Never assume healthy without metrics)
      const load1m =
        sysRes?.load_avg_1m !== undefined && sysRes?.load_avg_1m !== null
          ? Number(sysRes.load_avg_1m)
          : null;

      if (cpuVal !== null && !isNaN(cpuVal)) {
        const rounded = Math.round(cpuVal);
        if (cpuVal < 70) {
          setSysLoadText(`⚡ 正常 (CPU ${rounded}%)`);
          setSysLoadColor('var(--color-emerald)');
        } else if (cpuVal < 85) {
          setSysLoadText(`⚡ 较高 (CPU ${rounded}%)`);
          setSysLoadColor('var(--color-warning)');
        } else {
          setSysLoadText(`⚡ 过载 (CPU ${rounded}%)`);
          setSysLoadColor('var(--color-bearish)');
        }
      } else if (load1m !== null && !isNaN(load1m)) {
        setSysLoadText(`⚡ 正常 (负载 ${load1m.toFixed(2)})`);
        setSysLoadColor('var(--color-emerald)');
      } else {
        setSysLoadText('⚡ N/A (负载不可用)');
        setSysLoadColor('var(--text-muted)');
      }

      // 3. Aggregate Real Operational Alerts
      const newAlerts: MarketAlertItem[] = [];

      // A. Database storage health
      if (statusRes?.database && statusRes.database.healthy === false) {
        newAlerts.push({
          id: 'alt-storage-db',
          timestampUtc: statusRes.last_cycle_utc || new Date().toISOString(),
          severity: 'CRITICAL',
          title: '时序数据库异常 (Storage Unhealthy)',
          message: statusRes.database.error || 'TimescaleDB 数据库连接不可用或连接池耗尽。',
          domain: 'storage',
          resolved: resolvedAlertIds.has('alt-storage-db'),
        });
      }

      // B. Market collector error
      if (statusRes?.error_count && statusRes.error_count > 0 && statusRes.last_error) {
        newAlerts.push({
          id: 'alt-collector-last-err',
          timestampUtc: statusRes.last_cycle_utc || new Date().toISOString(),
          severity: 'WARNING',
          title: `采集引擎错误 (${statusRes.error_count} 次累计)`,
          message: statusRes.last_error,
          domain: 'collector',
          resolved: resolvedAlertIds.has('alt-collector-last-err'),
        });
      }

      // C. Ingestion incident status
      const incidentStatus = teleRes?.active_incident_status as string | undefined;
      if (incidentStatus === 'INCIDENT_ACTIVE') {
        newAlerts.push({
          id: 'alt-incident-active',
          timestampUtc: statusRes?.last_cycle_utc || new Date().toISOString(),
          severity: 'CRITICAL',
          title: '多源行情活跃故障 (Incident Active)',
          message: '采集管道处于故障状态：上游候选行情源全部不可用或连续多次周期超时。',
          domain: 'ingestion',
          resolved: resolvedAlertIds.has('alt-incident-active'),
        });
      } else if (incidentStatus === 'DEGRADED') {
        newAlerts.push({
          id: 'alt-incident-degraded',
          timestampUtc: statusRes?.last_cycle_utc || new Date().toISOString(),
          severity: 'WARNING',
          title: '行情采集防御性降级 (Ingestion Degraded)',
          message: '采集单周期耗时较长或触发了供应商切换，已自动启用容灾回退通道。',
          domain: 'ingestion',
          resolved: resolvedAlertIds.has('alt-incident-degraded'),
        });
      }

      // D. Recent failover transitions from real daemon logs
      const transLogs = (teleRes?.transition_logs as Array<Record<string, unknown>>) || [];
      const recentLogs = transLogs.slice(0, 3);
      for (const log of recentLogs) {
        const logId = `alt-trans-${log.timestamp_utc}-${log.symbol}`;
        const isCrit = log.to_state === 'ALL_PROVIDERS_UNAVAILABLE';
        newAlerts.push({
          id: logId,
          timestampUtc: String(log.timestamp_utc || ''),
          severity: isCrit ? 'CRITICAL' : 'WARNING',
          title: `容灾切换: ${log.symbol} (${log.from_state} → ${log.to_state})`,
          message: `切换原因: ${log.reason}，当前提供商: ${log.active_provider} (耗时 ${log.elapsed_ms}ms)`,
          domain: 'failover',
          resolved: resolvedAlertIds.has(logId),
        });
      }

      setAlerts(newAlerts);
    } catch (err) {
      console.warn('System health polling cycle failed:', err);
    }
  }, [resolvedAlertIds]);

  // Initial fetch and 4-second recurring polling
  useEffect(() => {
    fetchSystemHealth();
    const pollTimer = setInterval(fetchSystemHealth, 4000);
    return () => clearInterval(pollTimer);
  }, [fetchSystemHealth]);

  // Steady-state background health ticker (evaluates thresholds without clamping)
  useEffect(() => {
    const tickTimer = setInterval(() => {
      if (!lastCycleUtc) return;
      const appFreshResult = evaluateAppFreshness(lastCycleUtc, Date.now());
      setFreshnessText(appFreshResult.freshnessText);
      setFreshnessColor(appFreshResult.freshnessColor);
      setFreshnessTooltip(appFreshResult.freshnessTooltip);
    }, 5000);
    return () => clearInterval(tickTimer);
  }, [lastCycleUtc]);

  const handleToggleResolveAlert = useCallback((id: string) => {
    setResolvedAlertIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      try {
        localStorage.setItem('market_hub_resolved_alert_ids', JSON.stringify(Array.from(next)));
      } catch {
        // ignore
      }
      return next;
    });
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, resolved: !a.resolved } : a))
    );
  }, []);

  const unreadAlerts = alerts.filter((a) => !a.resolved).length;
  const hasCriticalAlert = alerts.some((a) => !a.resolved && a.severity === 'CRITICAL');

  return (
    <div className="app-layout">
      {/* Mobile Backdrop Overlay */}
      {isMobileOpen && (
        <div
          className="mobile-backdrop show"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Left Collapsible Navigation Rail */}
      <SidebarRail
        activeView={activeView}
        onSelectView={handleSelectView}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        isMobileOpen={isMobileOpen}
        currentRealm={currentRealm}
        onSelectRealm={handleSelectRealm}
      />

      {/* Main Content Area */}
      <div className="main-wrapper">
        {/* Sticky Top Telemetry Header */}
        <TopHeader
          activeView={activeView}
          currentRealm={currentRealm}
          onSelectRealm={handleSelectRealm}
          colorMode={colorMode}
          onToggleColorMode={handleToggleColorMode}
          alertCount={unreadAlerts}
          hasCriticalAlert={hasCriticalAlert}
          cpuPercent={cpuPercent}
          ramPercent={ramPercent}
          diskPercent={diskPercent}
          uptimeText={uptimeText}
          freshnessText={freshnessText}
          freshnessColor={freshnessColor}
          ashareSessionText={ashareSessionStatus}
          sysLoadText={sysLoadText}
          sysLoadColor={sysLoadColor}
          onOpenAlerts={() => setIsAlertsOpen(true)}
          onToggleMobileMenu={() => setIsMobileOpen(!isMobileOpen)}
          theme={theme}
          onToggleTheme={handleToggleTheme}
        />

        {/* Viewport Rendering */}
        <main className="content-viewport" id="main-content" role="main">
          {activeView === 'view-crypto-brief' && <CryptoAIBriefView colorMode={colorMode} />}
          {activeView === 'view-crypto-tactical-deck' && <TacticalCombatDeckView />}
          {activeView === 'view-crypto-strategist' && <CryptoStrategistView />}
          {activeView === 'view-trade-advisory' && <TradeAdvisoryBlueprintView />}
          {activeView === 'view-market-board-preview' && <MultiAssetMarketBoardPreview />}
          {activeView === 'view-trend-weather' && <TrendWeatherGrid />}
          {activeView === 'view-indicators-matrix' && <IndicatorsMatrixView />}
          {activeView === 'view-market-feeds' && <MarketFeedsView />}
          {activeView === 'view-regime-matrix' && <RegimeMatrixView />}
          {(activeView === 'view-regime-decision' || activeView === 'view-backtest-crypto') && (
            <RegimeDecisionBacktestView />
          )}
          {activeView === 'view-backtest-ashare' && (
            <div className="quantitative-view-container" style={{ width: '100%' }}>
              <div className="view-card" style={{ padding: '28px', background: 'var(--bg-surface-elevated)', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                <div className="bilingual-card-title" style={{ marginBottom: '12px' }}>
                  <h2 className="bilingual-card-title-zh" style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🇨🇳</span> 中国A股量化回测中枢
                  </h2>
                  <span className="bilingual-card-subtitle-en">A-SHARE FACTOR &amp; STRATEGY LAB</span>
                </div>
                <p style={{ margin: '0 0 16px 0', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  规划中：支持 5000+ A股多因子打分回测、主力资金流入/流出事件驱动回测、潜伏雷达低吸高抛区间胜率与盈亏比归因分析。
                </p>
                <div style={{ display: 'inline-flex', gap: '10px' }}>
                  <span className="terminal-badge" style={{ padding: '4px 8px', borderRadius: '4px', background: 'var(--badge-info-bg)', color: 'var(--color-cyan)', fontSize: '0.8rem' }}>
                    待立项开发 · SPEC-MARKET_HUB-ASHARE-BACKTEST
                  </span>
                </div>
              </div>
            </div>
          )}
          {activeView === 'view-backtest-usequity' && (
            <div className="quantitative-view-container" style={{ width: '100%' }}>
              <div className="view-card" style={{ padding: '28px', background: 'var(--bg-surface-elevated)', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                <div className="bilingual-card-title" style={{ marginBottom: '12px' }}>
                  <h2 className="bilingual-card-title-zh" style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🇺🇸</span> 美股量化回测中枢
                  </h2>
                  <span className="bilingual-card-subtitle-en">US EQUITY TREND &amp; EVENT LAB</span>
                </div>
                <p style={{ margin: '0 0 16px 0', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  规划中：支持纳斯达克 100 与标普 500 成分股动量回测、美联储议息与宏观大事件冲击响应分析、跨资产因果时延验证。
                </p>
                <div style={{ display: 'inline-flex', gap: '10px' }}>
                  <span className="terminal-badge" style={{ padding: '4px 8px', borderRadius: '4px', background: 'var(--badge-info-bg)', color: 'var(--color-cyan)', fontSize: '0.8rem' }}>
                    待立项开发 · SPEC-MARKET_HUB-USEQUITY-BACKTEST
                  </span>
                </div>
              </div>
            </div>
          )}
          {activeView === 'view-backtest-macro' && (
            <div className="quantitative-view-container" style={{ width: '100%' }}>
              <div className="view-card" style={{ padding: '28px', background: 'var(--bg-surface-elevated)', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                <div className="bilingual-card-title" style={{ marginBottom: '12px' }}>
                  <h2 className="bilingual-card-title-zh" style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🌐</span> 宏观大宗跨资产回测中枢
                  </h2>
                  <span className="bilingual-card-subtitle-en">MACRO &amp; COMMODITIES CORRIDOR LAB</span>
                </div>
                <p style={{ margin: '0 0 16px 0', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  规划中：覆盖 15 大类全球宏观情势资产走廊（黄金、原油、美元指数、美债收益率等）跨周期对冲与宏观情势套利回测。
                </p>
                <div style={{ display: 'inline-flex', gap: '10px' }}>
                  <span className="terminal-badge" style={{ padding: '4px 8px', borderRadius: '4px', background: 'var(--badge-info-bg)', color: 'var(--color-cyan)', fontSize: '0.8rem' }}>
                    待立项开发 · SPEC-MARKET_HUB-MACRO-BACKTEST
                  </span>
                </div>
              </div>
            </div>
          )}
          {activeView === 'view-ashare-screener' && (
            <AShareScreenerView colorMode={colorMode} onToggleColorMode={handleToggleColorMode} />
          )}
          {activeView === 'view-ashare-watchlist' && (
            <AShareWatchlistView colorMode={colorMode} />
          )}
          {activeView === 'view-ashare-index-radar' && (
            <AShareIndexRadarView colorMode={colorMode} onToggleColorMode={handleToggleColorMode} />
          )}
          {activeView === 'view-ashare-ambush-radar' && (
            <AShareAmbushRadarView colorMode={colorMode} onToggleColorMode={handleToggleColorMode} />
          )}
          {activeView === 'view-ashare-ct-matrix' && (
            <AShareCTMatrixView colorMode={colorMode} />
          )}
          {activeView === 'view-ashare-pattern-tags' && (
            <ASharePatternTagsView colorMode={colorMode} />
          )}
          {activeView === 'view-ashare-sector-rotation' && (
            <AShareSectorRotationView />
          )}
          {activeView === 'view-ashare-main-profile' && (
            <AShareMainProfileView />
          )}
          {activeView === 'view-macro-regime' && (
            <GlobalMacroRegimeView colorMode={colorMode} />
          )}
          {activeView === 'view-macro-market' && <MacroMarketView />}
          {activeView === 'view-dispatcher-cockpit' && <TaskDispatcherCockpit />}
          {activeView === 'view-multiasset-health' && <MultiAssetHealthCenter />}
          {activeView === 'view-dr-audit' && <DrAuditLogView />}
          {activeView === 'view-infra-telemetry' && <DataCenterHub />}
          {activeView === 'view-grounding-hub' && <ProjectGroundingView />}
          {activeView === 'view-integrations-audit' && <IntegrationsAuditHub />}
          {activeView === 'view-settings' && <SettingsView />}
        </main>

        {/* Realtime Operational Telemetry & Infrastructure SSOT Footer */}
        <footer
          className="app-footer"
          style={{
            borderTop: '1px solid var(--border-subtle)',
            padding: '10px 140px 10px 24px',
            background: 'var(--bg-surface)',
            fontSize: '0.78rem',
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-mono, monospace)',
          }}
        >
          <div
            style={{
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            {/* Storage SSOT & Worker Cluster */}
            <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ opacity: 0.6 }}>DB-1:</span>
                <strong>TimescaleDB (CRYPTO_PRIMARY)</strong>
                <span
                  style={{
                    display: 'inline-block',
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: opTelemetry.cryptoDbConnected === true ? 'var(--color-emerald)' : opTelemetry.cryptoDbConnected === false ? 'var(--color-bearish)' : 'var(--text-muted)',
                  }}
                  title={opTelemetry.cryptoDbConnected === true ? 'Crypto Primary DB Connected' : opTelemetry.cryptoDbConnected === false ? 'Crypto Primary DB Disconnected' : 'Crypto DB Status N/A'}
                />
                <span style={{ fontSize: '0.72rem', color: opTelemetry.cryptoDbConnected === true ? 'var(--color-emerald)' : opTelemetry.cryptoDbConnected === false ? 'var(--color-bearish)' : 'var(--text-muted)' }}>
                  {opTelemetry.cryptoDbConnected === true ? 'CONNECTED' : opTelemetry.cryptoDbConnected === false ? 'DISCONNECTED' : 'N/A'}
                </span>
              </span>

              <span style={{ color: 'var(--border-subtle)' }}>|</span>

              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ opacity: 0.6 }}>DB-2:</span>
                <strong>TimescaleDB (MULTIASSET_ACTIVE)</strong>
                <span
                  style={{
                    display: 'inline-block',
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: opTelemetry.multiAssetDbConnected === true ? 'var(--color-emerald)' : opTelemetry.multiAssetDbConnected === false ? 'var(--color-bearish)' : 'var(--text-muted)',
                  }}
                  title={opTelemetry.multiAssetDbConnected === true ? 'MultiAsset Active DB Connected' : opTelemetry.multiAssetDbConnected === false ? 'MultiAsset Active DB Disconnected' : 'MultiAsset DB Status N/A'}
                />
                <span style={{ fontSize: '0.72rem', color: opTelemetry.multiAssetDbConnected === true ? 'var(--color-emerald)' : opTelemetry.multiAssetDbConnected === false ? 'var(--color-bearish)' : 'var(--text-muted)' }}>
                  {opTelemetry.multiAssetDbConnected === true ? 'CONNECTED' : opTelemetry.multiAssetDbConnected === false ? 'DISCONNECTED' : 'N/A'}
                </span>
              </span>

              <span style={{ color: 'var(--border-subtle)' }}>|</span>

              {/* Ingress / Worker Scheduler Cluster */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ opacity: 0.6 }}>WORKER:</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  EOD Ingress ({opTelemetry.workerStatus})
                </span>
                {opTelemetry.eodSyncedDate && (
                  <span style={{ color: 'var(--accent-cyan)', fontSize: '0.72rem' }}>
                    [{opTelemetry.eodSyncedDate}]
                  </span>
                )}
              </span>
            </div>

            {/* Variety-Differentiated Realtime Telemetry Capsules */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* 1. Crypto Realm: Freshness (<3s) + 1m Completeness */}
              {currentRealm === 'crypto' && (
                <div
                  id="footer-crypto-telemetry"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 12px',
                    minWidth: '470px',
                    borderRadius: '4px',
                    background: 'var(--bg-card, rgba(255, 255, 255, 0.04))',
                    border: '1px solid var(--border-subtle)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  title="加密货币数据流：秒级时效探针 + 7大核心资产1m蜡烛缺棒率"
                >
                  <span style={{ whiteSpace: 'nowrap' }}>🪙 <strong>加密质量:</strong></span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>新鲜度</span>
                  <span
                    id="footer-crypto-freshness"
                    role={freshnessText.includes('延迟') || freshnessText.includes('中断') || freshnessText.includes('N/A') ? 'alert' : undefined}
                    title={freshnessTooltip}
                    style={{
                      display: 'inline-block',
                      minWidth: '110px',
                      color: freshnessColor,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                      cursor: 'help',
                    }}
                  >
                    {freshnessText}
                  </span>
                  <span style={{ color: 'var(--border-subtle)' }}>·</span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>完整度</span>
                  <span
                    id="footer-crypto-completeness"
                    style={{
                      display: 'inline-block',
                      minWidth: '56px',
                      color: cryptoCompleteness === '100.00%' ? 'var(--color-emerald)' : cryptoCompleteness === 'N/A' ? 'var(--text-muted)' : 'var(--color-warning)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {cryptoCompleteness}
                  </span>
                  <span style={{ opacity: 0.6, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                    ({cryptoMissingCount !== null ? `${cryptoMissingCount} 缺棒` : '无统计'} · 7资产1m流)
                  </span>
                </div>
              )}

              {/* 2. A-Share Realm: Dual Session Status + Universe Coverage */}
              {currentRealm === 'ashare' && (
                <div
                  id="footer-ashare-telemetry"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 12px',
                    minWidth: '470px',
                    borderRadius: '4px',
                    background: 'var(--bg-card, rgba(255, 255, 255, 0.04))',
                    border: '1px solid var(--border-subtle)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  title="中国A股时效与标的覆盖度监控"
                >
                  <span style={{ whiteSpace: 'nowrap' }}>🇨🇳 <strong>A股市效:</strong></span>
                  <span id="footer-ashare-session" style={{ color: 'var(--text-primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {ashareSessionStatus}
                  </span>
                  <span style={{ color: 'var(--border-subtle)' }}>·</span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>标的覆盖</span>
                  <span id="footer-ashare-coverage" style={{ color: ashareCoverage !== 'N/A' ? 'var(--color-emerald)' : 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {ashareCoverage}
                  </span>
                  {ashareCoveragePct !== 'N/A' && (
                    <span style={{ opacity: 0.6, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                      ({ashareCoveragePct})
                    </span>
                  )}
                </div>
              )}

              {/* 3. Macro Realm: EOD Fix Synchronized + Major Commodities Coverage */}
              {currentRealm === 'macro' && (
                <div
                  id="footer-macro-telemetry"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 12px',
                    minWidth: '470px',
                    borderRadius: '4px',
                    background: 'var(--bg-card, rgba(255, 255, 255, 0.04))',
                    border: '1px solid var(--border-subtle)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  title="全球宏观与大宗商品：日频定盘同步 + 资产类别覆盖"
                >
                  <span style={{ whiteSpace: 'nowrap' }}>🌐 <strong>宏观大宗:</strong></span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>定盘时效</span>
                  <span style={{ color: opTelemetry.eodSyncedDate ? 'var(--accent-cyan)' : 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {opTelemetry.eodSyncedDate ? `日频定盘 [${opTelemetry.eodSyncedDate}]` : '定盘未同步 (N/A)'}
                  </span>
                  <span style={{ color: 'var(--border-subtle)' }}>·</span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>核心标的</span>
                  <span style={{ color: 'var(--color-emerald)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    美债 / 黄金 / 原油 / DXY
                  </span>
                  <span style={{ opacity: 0.6, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                    (已跟踪)
                  </span>
                </div>
              )}

              {/* 4. Backtest Realm: Ephemeral Quantitative Lab Engine Telemetry */}
              {currentRealm === 'backtest' && (
                <div
                  id="footer-backtest-telemetry"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 12px',
                    minWidth: '470px',
                    borderRadius: '4px',
                    background: 'var(--bg-card, rgba(255, 255, 255, 0.04))',
                    border: '1px solid var(--border-subtle)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  title="跨资产量化回测中枢引擎遥测"
                >
                  <span style={{ whiteSpace: 'nowrap' }}>🧪 <strong>回测中枢:</strong></span>
                  <span style={{ color: 'var(--color-cyan)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    跨品种架构 (Active)
                  </span>
                  <span style={{ color: 'var(--border-subtle)' }}>·</span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>归因引擎</span>
                  <span style={{ color: 'var(--color-emerald)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    EPHEMERAL_V1
                  </span>
                  <span style={{ opacity: 0.6, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                    (纯内存沙盒 · 零持久化污染)
                  </span>
                </div>
              )}

              {/* 5. System Realm: Multi-Asset Datacenter Aggregated Telemetry */}
              {currentRealm === 'system' && (
                <div
                  id="footer-system-telemetry"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 12px',
                    minWidth: '470px',
                    borderRadius: '4px',
                    background: 'var(--bg-card, rgba(255, 255, 255, 0.04))',
                    border: '1px solid var(--border-subtle)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  title="全域基础设施综合时效与多品种监控"
                >
                  <span style={{ whiteSpace: 'nowrap' }}>⚙️ <strong>全域监控:</strong></span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>加密</span>
                  <span
                    id="footer-system-freshness"
                    role={freshnessText.includes('延迟') || freshnessText.includes('中断') || freshnessText.includes('异常') || freshnessText.includes('N/A') ? 'alert' : undefined}
                    title={freshnessTooltip}
                    style={{ color: freshnessColor, fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {freshnessText}
                  </span>
                  <span style={{ color: 'var(--border-subtle)' }}>·</span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>A股</span>
                  <span style={{ color: ashareCoverage !== 'N/A' ? 'var(--color-emerald)' : 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {ashareCoverage !== 'N/A' ? `${ashareCoverage} (${ashareCoveragePct})` : 'N/A'}
                  </span>
                  <span style={{ color: 'var(--border-subtle)' }}>·</span>
                  <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>宏观</span>
                  <span style={{ color: opTelemetry.eodSyncedDate ? 'var(--accent-cyan)' : 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {opTelemetry.eodSyncedDate ? '日频已同步' : '未同步'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </footer>
      </div>

      {/* Global Floating Copilot Hub Widget (Fixed Bottom-Right) */}
      <GlobalAgentCopilotHub currentView={activeView} />

      {/* Alerts Sliding Inspection Drawer */}
      <AlertsDrawer
        isOpen={isAlertsOpen}
        onClose={() => setIsAlertsOpen(false)}
        alerts={alerts}
        onToggleResolve={handleToggleResolveAlert}
      />
    </div>
  );
};

export default App;
